<script setup lang="ts">
import { computed, inject } from 'vue'
import { useRoute } from 'vue-router'
import { useLegacyBootstrap } from '@/composables/useLegacyBootstrap'
import { useIdleTimeout } from '@/composables/useIdleTimeout'
import { useAuthTabSync } from '@/composables/useAuthTabSync'
import { useAuthEffects } from '@/composables/useAuthEffects'
import { useAuth } from '@/composables/useAuth'
import { useMessagesStore } from '@/stores/messagesStore'
import { $eventBusKey } from '@/utils/eventBus'
import SSnackbar from '@/components/singleton/SSnackbar/SSnackbar.vue'
// LEGACY components kept from the old App.vue. Deliberately excluded:
// - TheSnackbar — replaced by SSnackbar + the bus forwarder below
import Payments from '@old/components/payments'
import Loader from '@old/components/Loader.vue'
import Navigation from '@old/components/navigation/index.vue'
import MediaDialog from '@old/components/media_dialog/index.vue'
import Respite from '@old/components/respite/index.vue'
import Amortization from '@old/components/amortization/index.vue'
import ProfileDialog from '@old/components/profile_dialog/index.vue'
import MessageDialog from '@old/components/message_dialog/index.vue'
import { store as legacyStoreUntyped } from '@old/store'

interface LegacyDialogState {
  context?: unknown
  document?: unknown
  text?: string
  title?: string
}

const store = legacyStoreUntyped as {
  state: {
    profile_dialog: LegacyDialogState | null
    message_dialog: LegacyDialogState | null
    dont_show_profile_dialog?: boolean
    [key: string]: unknown
  }
}

const route = useRoute()

const { loading } = useLegacyBootstrap()
useIdleTimeout()
useAuthTabSync()
useAuthEffects()
const { isAuthenticated } = useAuth()

// LEGACY: forward the old bus 'snackbar' event into the new messagesStore so
// there is exactly ONE snackbar UI (SSnackbar) during the transition.
const messagesStore = useMessagesStore()
const bus = inject($eventBusKey)
bus?.on('snackbar', (m) => {
  messagesStore.addMessage(m.text)
})

const loginView = computed(() => route.name === 'Authenticate')

const displayProfileDialog = computed(
  () =>
    store.state.dont_show_profile_dialog !== true &&
    store.state.profile_dialog != null &&
    Object.keys(store.state.profile_dialog).length > 0
)

const displayMessageDialog = computed(
  () => store.state.message_dialog != null && Object.keys(store.state.message_dialog).length > 0
)
</script>

<template>
  <v-app id="app">
    <!-- Unmounted (not just hidden) when logged out: the drawer is local state
         inside the legacy component and would otherwise stay open and clickable
         after an idle logout. Every route where the nav shows requires auth. -->
    <navigation v-if="isAuthenticated" />
    <v-main>
      <v-container
        :class="{ 'app-width': !loginView, 'fill-height': loginView }"
        :fluid="loginView"
        class="position-relative"
      >
        <profile-dialog
          v-if="displayProfileDialog"
          :context="store.state.profile_dialog?.context"
          :document="store.state.profile_dialog?.document"
        />
        <message-dialog
          v-if="displayMessageDialog"
          :text="store.state.message_dialog?.text"
          :title="store.state.message_dialog?.title"
        />
        <!-- LEGACY dialogs (die with the old views). Auth-gated ones are
             UNMOUNTED on logout: they open via one-way watchers on Vuex keys
             (key set → local internal_value = true) and nothing ever closes
             them when the key is cleared — an open dialog would survive an
             idle logout onto the login screen. The Vuex keys themselves are
             reset by useAuthEffects.resetLegacyState (see LEGACY note there),
             otherwise stale state would auto-reopen the dialog on remount at
             the NEXT login. -->
        <media-dialog v-if="isAuthenticated" />
        <!-- payments must stay mounted while logged out (pay-with-OCR from the
             login screen uses the public token), so it is force-REMOUNTED on
             auth transitions via :key instead — remount resets its local
             internal_value and closes any open dialog. -->
        <payments :key="`payments-${isAuthenticated}`" />
        <amortization v-if="isAuthenticated" />
        <respite v-if="isAuthenticated" />
        <loader v-if="loading" />
        <router-view v-show="!loading" />
        <s-snackbar />
      </v-container>
    </v-main>
  </v-app>
</template>

<style lang="sass">
.v-main
    @media (max-width: 960px)
        flex: 0
</style>
